import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Ten digits, first digit 6-9. The Indian mobile numbering plan. */
const NATIONAL_MOBILE = /^[6-9]\d{9}$/;

/** Country code, optional leading zero, and the separators people actually type. */
const DECORATION = /[\s()\-.]/g;

/**
 * Strips formatting and the +91 / 0091 / 0 prefixes down to the ten national
 * digits.
 *
 * Normalising before validating matters because a user pasting a number out of
 * a contact list gets `+91 98765 43210`, and rejecting that as "invalid" while
 * the same number typed bare is accepted is the kind of inconsistency that
 * gets a login screen mistrusted.
 */
export function normaliseMobile(value: string): string {
  const bare = value.replace(DECORATION, '').replace(/^\+/, '');
  if (bare.startsWith('0091')) return bare.slice(4);
  if (bare.startsWith('91') && bare.length === 12) return bare.slice(2);
  if (bare.startsWith('0') && bare.length === 11) return bare.slice(1);
  return bare;
}

export function isValidMobile(value: string): boolean {
  return NATIONAL_MOBILE.test(normaliseMobile(value));
}

/**
 * Mobile-number validator.
 *
 * Empty is VALID here: emptiness is `Validators.required`'s job, and a control
 * that reports both `required` and `mobile` on a blank field renders two error
 * messages for one mistake.
 */
export function mobileValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw: unknown = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw !== 'string') return { mobile: true };
    return isValidMobile(raw) ? null : { mobile: true };
  };
}
